import { watch } from 'vue'

const PREFIX = 'os:form:'

type Box = { value: unknown }

/**
 * Keep typed form fields in localStorage until `clear()` after a successful submit.
 * Restore is synchronous at setup so later loaders can respect saved values.
 *
 * Storage can be unavailable (private mode, blocked cookies, a non-browser runtime):
 * the draft then simply does not persist, which must never break the form.
 */
export function useFormDraft(
    name: string,
    fields: Record<string, Box>,
    options?: { enabled?: () => boolean },
): { clear: () => void } {
    const key = `${PREFIX}${name}`

    function read(): Record<string, unknown> | null {
        try {
            const raw = localStorage.getItem(key)
            if (!raw) {
                return null
            }
            const parsed: unknown = JSON.parse(raw)
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return null
            }
            return parsed as Record<string, unknown>
        } catch {
            return null
        }
    }

    function write(): void {
        if (options?.enabled && !options.enabled()) {
            return
        }
        const data: Record<string, unknown> = {}
        for (const [field, box] of Object.entries(fields)) {
            data[field] = box.value
        }
        try {
            localStorage.setItem(key, JSON.stringify(data))
        } catch {
            // No storage: the draft lives only as long as the page does.
        }
    }

    const saved = read()
    if (saved) {
        for (const [field, box] of Object.entries(fields)) {
            if (field in saved) {
                box.value = saved[field]
            }
        }
    }

    watch(() => Object.fromEntries(Object.entries(fields).map(([field, box]) => [field, box.value])), write, {
        deep: true,
    })

    return {
        clear: () => {
            try {
                localStorage.removeItem(key)
            } catch {
                // Nothing was stored in the first place.
            }
        },
    }
}
